import { defineMessage, t } from "@lingui/macro"
import { pipe } from "fp-ts/function"
import * as D from "io-ts/Decoder"
import { useRouter } from "next/router"

import * as Auction from "@/domains/auctions"
import { AuctionGridSkeleton } from "@/domains/auctions/view/grid"
import { AuctionsGridSection } from "@/domains/auctions/view/grid-section"
import * as Country from "@/domains/countries"
import { PaginationResult } from "@/domains/pagination/model"
import { UrlSlug } from "@/domains/url-slug"
import { useI18n } from "@/lib/i18n"
import { laxPartial } from "@/lib/lax-partial"
import { NumberFromString } from "@/lib/number"
import { useParsedQuery } from "@/lib/router"
import { Breadcrumbs } from "@/ui/breadcrumbs"
import { Container } from "@/ui/container"
import { PageNav } from "@/ui/page-nav"
import { Stack } from "@/ui/stack"
import { Text } from "@/ui/text"

import { Layout } from "../_layout"

const itemsPerPage = 24

function CountryDetail(props: {
  country: Country.Country
  auctions: PaginationResult<Auction.AuctionPreview>
  page: number
  sorting?: Auction.Sorting
}) {
  const { country, auctions, page, sorting } = props
  const { localize } = useI18n()
  const router = useRouter()
  const countryName = localize(country.name) ?? ""

  const breadcrumbs = [
    {
      label: t({
        id: "centuryList.title",
        message: "Země",
      }),
    },
    {
      label: countryName,
    },
  ]

  return (
    <>
      <Container
        css={{
          py: "$3",
          "@lg": {
            py: "$6",
          },
        }}
      >
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <Text
          as="h1"
          size={{ "@initial": "4xl", "@md": "5xl" }}
          weight="bold"
          css={{ mb: "$4", mt: "$4" }}
        >
          {countryName}
        </Text>
      </Container>
      <Container css={{ pb: "$12" }}>
        <Stack direction="column" gap={12} css={{ mb: "$12" }}>
          <AuctionsGridSection
            title={t({
              id: "centuryDetail.artworks.title",
              message: "Díla",
            })}
            auctions={auctions.data}
            gridProps={{ cardProps: { hideAuthor: true } }}
            barProps={{
              sorting,
              onSortingChange: (newSorting) => {
                router.push({
                  pathname: Country.hrefOf(country),
                  query: {
                    page,
                    sorting: newSorting,
                  },
                })
              },
              sortingLabels: {
                default: defineMessage({
                  id: "auctions.sortBy.dateOfAuction",
                  message: "Datum dražby",
                }),
              },
            }}
          />
        </Stack>
        <PageNav
          page={page}
          hrefTemplate={(newPage) => ({
            pathname: Country.hrefOf(country),
            query: {
              page: newPage,
              sorting,
            },
          })}
          totalPages={Math.ceil(auctions.totalItems / itemsPerPage)}
        />
      </Container>
    </>
  )
}

function Skeleton() {
  return (
    <Container css={{ py: "$12" }}>
      <AuctionGridSkeleton view="gridCompact" items={itemsPerPage} />
    </Container>
  )
}

const Query = pipe(
  D.struct({
    id: UrlSlug,
  }),
  D.intersect(
    laxPartial({
      page: NumberFromString,
      sorting: Auction.Sorting,
    })
  ),
  D.map(({ page = 1, sorting = "default", ...rest }) => ({
    ...rest,
    sorting,
    page,
  }))
)

export function CountryDetailPageContent(props: {
  query: D.TypeOf<typeof Query>
}) {
  const { id, page, sorting } = props.query

  const countryQuery = Country.useCountryQuery(id)
  const auctionsQuery = Auction.useAuctionsQuery({
    country: id,
    sorting,
    page,
    itemsPerPage,
  })

  return (
    <Layout>
      {countryQuery.isSuccess && auctionsQuery.isSuccess ? (
        <CountryDetail
          country={countryQuery.data}
          auctions={auctionsQuery.data}
          page={page}
          sorting={sorting}
        />
      ) : (
        <Skeleton />
      )}
    </Layout>
  )
}

export default function CountryDetailPage() {
  const query = useParsedQuery(Query)

  if (query === null) {
    return null
  }

  return <CountryDetailPageContent query={query} />
}
